import { RestClient } from "./restClient";

export interface KnowledgeBackendSetting {
  /** The backend in effect: "opensearch" | "graph". */
  value: string;
  /** "database" when an admin chose it, "default" when nothing is set. Lets
   *  the panel say whether the shown value is a decision or a fallback. */
  source: "database" | "default";
  updated_by: string | null;
  updated_at: string | null;
  /** Valid values, served by the API so the dropdown cannot drift from the
   *  server's SEARCH_BACKENDS. */
  options: string[];
}

/** Session-scoped cache of the setting.
 *
 * Module scope, not component state: the KB settings panel unmounts every
 * time the user switches tab in Organization settings, so a plain
 * `useEffect` fetch would issue a request on every click. This survives
 * unmounts and lives until a page reload.
 *
 * Correctness rests on `save()` replacing it — the value changes only through
 * this module, so a stale entry can only appear if another admin changes the
 * setting in a different browser, and the next reload resolves that.
 */
let cached: KnowledgeBackendSetting | null = null;

/** Drop the cached value so the next `load()` refetches. Exported for tests
 *  and for a future explicit "Refresh" affordance. */
export function clearKbSettingCache(): void {
  cached = null;
}

/** Wraps /v1/settings/knowledge-backend. Methods throw RestError on non-2xx —
 *  notably 403 for a non-platform-admin, which the panel renders rather than
 *  swallowing: a silent empty panel looks like a bug. */
export class KbSettingsApi {
  constructor(private client: RestClient) {}

  /** The current setting, from cache when already fetched this session. */
  async load(): Promise<KnowledgeBackendSetting> {
    if (cached) return cached;
    cached = await this.client.get<KnowledgeBackendSetting>(
      "/v1/settings/knowledge-backend",
    );
    return cached;
  }

  /** Change the backend. The response — not the submitted value — becomes the
   *  cache, because the server owns normalisation and the audit fields. */
  async save(value: string): Promise<KnowledgeBackendSetting> {
    const saved = await this.client.put<KnowledgeBackendSetting>(
      "/v1/settings/knowledge-backend",
      { value },
    );
    cached = saved;
    return saved;
  }
}
